import React, { useEffect, useMemo, useRef } from 'react';

const DEFAULT_TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['link'],
  ['clean']
];

const EMPTY_EDITOR_VALUE = '<p><br></p>';

const normalizeValue = (value) => {
  if (!value || value === EMPTY_EDITOR_VALUE) return '';
  return value;
};

const RichTextOption = ({ defaultValue, onChange, optionId = '', toolbarOptions = DEFAULT_TOOLBAR_OPTIONS, editorStyle }) => {
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const toolbarRef = useRef(null);
  const fallbackValue = useMemo(() => normalizeValue(defaultValue), [defaultValue]);
  const hasQuill = typeof window !== 'undefined' && Boolean(window.Quill);
  const resolvedToolbarOptions = Array.isArray(toolbarOptions) && toolbarOptions.length > 0
    ? toolbarOptions
    : DEFAULT_TOOLBAR_OPTIONS;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!hasQuill || !editorRef.current || quillRef.current) return undefined;

    const quill = new window.Quill(editorRef.current, {
      theme: 'snow',
      placeholder: 'Write a short sub header description...',
      modules: {
        toolbar: resolvedToolbarOptions
      }
    });

    const initialValue = normalizeValue(defaultValue);
    if (initialValue) {
      quill.clipboard.dangerouslyPasteHTML(initialValue, 'silent');
    }

    quill.on('text-change', (_delta, _oldDelta, source) => {
      if (source !== 'user') return;
      const nextValue = normalizeValue(quill.root.innerHTML);
      onChangeRef.current?.(nextValue);
    });

    quillRef.current = quill;
    toolbarRef.current = quill.getModule('toolbar')?.container || null;

    return () => {
      if (toolbarRef.current?.parentNode) {
        toolbarRef.current.parentNode.removeChild(toolbarRef.current);
      }
      toolbarRef.current = null;
      quillRef.current = null;
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
    };
  }, [hasQuill, resolvedToolbarOptions]);

  useEffect(() => {
    if (!quillRef.current) return;

    const quill = quillRef.current;
    const nextValue = normalizeValue(defaultValue);
    const currentValue = normalizeValue(quill.root.innerHTML);

    if (currentValue === nextValue) return;

    if (!nextValue) {
      quill.setText('', 'api');
      return;
    }

    quill.clipboard.dangerouslyPasteHTML(nextValue, 'api');
  }, [defaultValue]);

  if (!hasQuill) {
    return (
      <div className="un-textarea-option">
        <textarea
          style={{ width: '100%' }}
          rows={10}
          type="text"
          value={fallbackValue}
          onChange={(event) => onChange?.(event.target.value)}
        />
      </div>
    );
  }

  return (
    <div
      className={`un-rich-text-option${optionId ? ` un-rich-text-option--${optionId}` : ''}`}
      style={{ width: '100%', minWidth: 0 }}
    >
      <div ref={editorRef} style={{ minHeight: 180, background: '#fff', ...(editorStyle || {}) }} />
    </div>
  );
};

export default RichTextOption;
